import { Job, CommandJob, Step } from "~/components/build/Show/lib/types";
import { isParallelCommandStep, isMatrixCommandStep } from "~/lib/pipeline/Step/CommandStep";
import {
  mostRecentParallelJobs,
  mostRecentMatrixJobs,
} from "~/components/build/Show/lib/BuildContext";

/**
 * Selects the "best" job to represent a step when it has multiple jobs.
 *
 * @param step - The step to find a job for
 * @param jobs - Array of jobs belonging to the step
 * @param allJobs - Map of all jobs (needed for retry chain traversal)
 * @returns Job UUID of the selected job, or null if no jobs exist
 */
export function getJobForStep(step: Step, jobs: Job[], allJobs: Map<string, Job>): string | null {
  if (!jobs || jobs.length === 0) {
    return null;
  }

  // Parallel steps → first job (best scroll position in Classic)
  if (isParallelCommandStep(step)) {
    const recentJobs = mostRecentParallelJobs(jobs as CommandJob[]);
    return recentJobs[0]?.id || null;
  }

  // Matrix steps → first job (best scroll position in Classic)
  if (isMatrixCommandStep(step)) {
    const recentJobs = mostRecentMatrixJobs(jobs as CommandJob[]);
    return recentJobs[0]?.id || null;
  }

  // All other step types → last job (latest/most relevant)
  // Covers: command (single), trigger
  const lastJob = jobs[jobs.length - 1];

  // For command jobs with retries, find the latest retry
  if (lastJob.type === "script") {
    const commandJob = lastJob as CommandJob;
    if (commandJob.retriedInJobUuid) {
      return findLatestRetryId(commandJob, allJobs);
    }
  }

  return lastJob.id;
}

/**
 * Follows the retry chain to find the latest retry for a job.
 *
 * @param job - Starting job
 * @param allJobs - Map of all jobs
 * @returns UUID of the latest retry in the chain
 */
function findLatestRetryId(job: CommandJob, allJobs: Map<string, Job>): string {
  if (job.retriedInJobUuid) {
    const retry = allJobs.get(job.retriedInJobUuid);
    if (retry && retry.type === "script") {
      return findLatestRetryId(retry as CommandJob, allJobs);
    }
  }
  return job.id;
}
